import React, { useState, useMemo } from 'react';
import { Check, Eye, X } from 'lucide-react';
import { BasicInfo, Customization } from '../types';
import { invitationTemplates } from '../data';
import { WeddingInvitation } from '../../../types/invitation';
import { InvitePage } from '../../InvitePage';
import { formatChineseDate } from '../../../lib/utils';

interface Step4Props {
  basicInfo: BasicInfo;
  customization: Customization;
  selectedTemplate: number | null;
  isGenerating: boolean;
}

export const Step4_Confirmation: React.FC<Step4Props> = ({
  basicInfo,
  customization,
  selectedTemplate,
  isGenerating,
}) => {
  const [showPreview, setShowPreview] = useState(false);

  const previewInvitation = useMemo<WeddingInvitation>(() => {
    const template = selectedTemplate !== null
      ? invitationTemplates.find(t => t.id === selectedTemplate)
      : null;

    return {
      id: 'preview',
      inviteCode: 'preview',
      brideName: basicInfo.brideName,
      groomName: basicInfo.groomName,
      weddingDate: basicInfo.date,
      weddingTime: basicInfo.time,
      venue: basicInfo.venue.name,
      address: basicInfo.venue.address,
      mapUrl: basicInfo.venue.mapUrl,
      hasWelcomeParty: basicInfo.hasWelcomeParty,
      hasWeddingCeremony: basicInfo.hasWeddingCeremony,
      hasReception: basicInfo.hasReception,
      showTransportInfo: basicInfo.showTransportInfo,
      showContactInfo: basicInfo.showContactInfo,
      transportDetails: basicInfo.transportDetails,
      welcomePartyVenue: basicInfo.welcomePartyVenue,
      welcomePartyAddress: basicInfo.welcomePartyAddress,
      welcomePartyTime: basicInfo.welcomePartyTime,
      ceremonyVenue: basicInfo.ceremonyVenue,
      ceremonyAddress: basicInfo.ceremonyAddress,
      ceremonyTime: basicInfo.ceremonyTime,
      receptionVenue: basicInfo.receptionVenue,
      receptionAddress: basicInfo.receptionAddress,
      receptionTime: basicInfo.receptionTime,
      contactPhone: basicInfo.contactPhone,
      contactEmail: basicInfo.contactEmail,
      template: template?.name || '',
      coverImage: customization.coverImage,
      welcomeMessage: customization.welcomeMessage,
      primaryColor: customization.primaryColor,
      createdAt: new Date(),
    };
  }, [basicInfo, customization, selectedTemplate]);

  const templateName = invitationTemplates.find(t => t.id === selectedTemplate)?.name ?? '未選擇';

  return (
    <div>
      <h3 className="text-xl font-semibold mb-6 flex items-center">
        <Check className="h-6 w-6 text-pink-500 mr-3" />
        最終確認與預覽
      </h3>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-4">婚禮資訊摘要</h4>
          <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">新人</span>
              <span className="font-medium">{basicInfo.brideName} & {basicInfo.groomName}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">日期</span>
              <span className="font-medium">{formatChineseDate(basicInfo.date)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">地點</span>
              <span className="font-medium">{basicInfo.venue.name}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">迎賓</span>
              <span className={`font-medium ${basicInfo.hasWelcomeParty ? 'text-green-600' : 'text-gray-400'}`}>
                {basicInfo.hasWelcomeParty ? '\u2713' : '\u2717'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">證婚儀式</span>
              <span className={`font-medium ${basicInfo.hasWeddingCeremony ? 'text-green-600' : 'text-gray-400'}`}>
                {basicInfo.hasWeddingCeremony ? '\u2713' : '\u2717'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">婚宴</span>
              <span className={`font-medium ${basicInfo.hasReception ? 'text-green-600' : 'text-gray-400'}`}>
                {basicInfo.hasReception ? '\u2713' : '\u2717'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">交通資訊</span>
              <span className={`font-medium ${basicInfo.showTransportInfo ? 'text-green-600' : 'text-gray-400'}`}>
                {basicInfo.showTransportInfo ? '\u2713' : '\u2717'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">聯絡方式</span>
              <span className={`font-medium ${basicInfo.showContactInfo ? 'text-green-600' : 'text-gray-400'}`}>
                {basicInfo.showContactInfo ? '\u2713' : '\u2717'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">範本</span>
              <span className="font-medium">{templateName}</span>
            </div>
            {customization.welcomeMessage && (
              <div className="pt-2 border-t border-gray-200">
                <span className="text-sm text-gray-600">歡迎訊息</span>
                <p className="text-sm mt-1 text-gray-800 italic line-clamp-2">
                  "{customization.welcomeMessage}"
                </p>
              </div>
            )}
          </div>
        </div>

        <div>
          <h4 className="text-lg font-medium text-gray-900 mb-4">邀請函預覽</h4>
          <div className="bg-gray-100 rounded-lg border border-gray-200 overflow-hidden">
            <div
              className="relative h-56"
              style={{
                backgroundImage: customization.coverImage
                  ? `url(${customization.coverImage})`
                  : 'linear-gradient(135deg, #fdf2f8, #f3e8ff)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              <div className="absolute inset-0 bg-black/30" />
              <div className="relative z-10 flex flex-col items-center justify-center h-full text-white px-4 text-center">
                <h5 className="text-2xl font-serif mb-1">
                  {basicInfo.brideName} & {basicInfo.groomName}
                </h5>
                <p className="text-sm opacity-90">{formatChineseDate(basicInfo.date)}</p>
              </div>
            </div>
            <div className="p-4">
              <button
                type="button"
                onClick={() => setShowPreview(true)}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-lg text-white font-medium transition-colors"
                style={{ backgroundColor: customization.primaryColor }}
              >
                <Eye className="h-5 w-5" />
                全螢幕預覽邀請函
              </button>
              <p className="text-xs text-gray-500 mt-2 text-center">
                點擊查看賓客收到的實際邀請函效果
              </p>
            </div>
          </div>
        </div>
      </div>

      {isGenerating && (
        <div className="text-center mt-6">
          <div className="inline-flex items-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-pink-500 mr-3"></div>
            <span className="text-gray-600">正在生成您的邀請函...</span>
          </div>
        </div>
      )}

      {showPreview && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm">
          <div className="absolute top-4 right-4 z-[60]">
            <button
              type="button"
              onClick={() => setShowPreview(false)}
              className="flex items-center gap-2 bg-white/90 hover:bg-white text-gray-800 px-4 py-2 rounded-full shadow-lg transition-colors"
            >
              <X className="h-5 w-5" />
              關閉預覽
            </button>
          </div>
          <div className="h-full overflow-y-auto">
            <InvitePage
              inviteCode="preview"
              weddingInvitation={previewInvitation}
            />
          </div>
        </div>
      )}
    </div>
  );
};
